import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

// Navigation bar supporting 5 code-editor workflow sections.
// Tracks active section via URL hash + scroll position.
// Supports desktop segmented-nav and collapsible mobile dropdown.
const NavBar = ({ currentStep }) => {
  const location = useLocation();
  const { hash } = location;

  // Controls visibility of mobile dropdown menu
  const [isOpen, setIsOpen] = useState(false);

  // Tracks which section is highlighted
  // Derived initially from location.hash
  const [activeSection, setActiveSection] = useState(hash.replace('#', '') || '');

  // Fixed step structure defining scroll targets + labels.
  // Order is sequential and determines scroll-based section activation.
  const steps = [
    { id: "data-preparation", label: "Data Preparation" },
    { id: "model-definition", label: "Model Definition" },
    { id: "training-configuration", label: "Training Config" },
    { id: "evaluation", label: "Evaluation" },
    { id: "visualization", label: "Visualization" },
  ];

  // When URL hash changes, auto-scroll to corresponding section.
  // This makes direct navigation sharable (copy-paste URL retains scroll position).
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {

        // Delay ensures page finishes layout before computing offsets.
        setTimeout(() => {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          
          // Hard offset aligns scroll target below sticky header + navbar.
          const offsetPosition = elementPosition - 180;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }, 100);
      }
      setActiveSection(hash.replace('#', ''));
    }
  }, [hash]);

  // Tracks scroll and updates active section dynamically.
  // Scroll logic:
  // Locks to final section when user reaches bottom
  // Otherwise highlights whichever section intersects scroll threshold (200px)
  useEffect(() => {
    const handleScroll = () => {
      const sections = steps.map(step => ({
        id: step.id,
        element: document.getElementById(step.id)
      }));

      // If user reaches bottom, force highlight on final section
      const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10;
      
      if (isAtBottom) {
        setActiveSection(steps[steps.length - 1].id);
        return;
      }

      const currentSection = sections.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 200 && rect.bottom >= 200;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close mobile menu when clicking a section link
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  // Manually scroll to a section AND update URL hash without full navigation
  // This prevents react-router re-mounts while giving proper back-navigation behavior
  const handleNavClick = (e, stepId) => {
    e.preventDefault();
    const element = document.getElementById(stepId);
    if (element) {
      // Get the element's position and account for sticky navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - 180; // Offset for sticky header + navbar
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      
      setActiveSection(stepId);
      window.history.pushState(null, '', `${location.pathname}#${stepId}`);
    }
    setIsOpen(false);
  };

  return (
    <div className="sticky top-20 left-0 right-0 z-40 w-full flex justify-center bg-white/95 backdrop-blur-sm shadow-md py-4 px-4">
      <div className="w-full max-w-5xl">
        {/* Desktop Navigation */}
        <div className="hidden md:flex w-full bg-white border border-[#E2E8F0] shadow-sm rounded-xl overflow-hidden">
          {steps.map((step, index) => (
            <Link
              key={step.id}
              to={`${location.pathname}#${step.id}`}
              onClick={(e) => handleNavClick(e, step.id)}
              className={`flex-1 text-center py-3 text-sm font-semibold transition 
                ${
                  activeSection === step.id
                    ? "bg-[#0EA5E9] text-white shadow-inner"
                    : "text-[#334155] hover:bg-[#E0F2FE]"
                }`}
            >
              {step.label}
            </Link>
          ))}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between bg-white border border-[#E2E8F0] shadow-sm rounded-xl px-4 py-3"
          >
            <span className="text-sm font-semibold text-[#334155]">
              {steps.find(s => s.id === activeSection)?.label || "Navigation"}
            </span>
            {isOpen ? (
              <X className="w-5 h-5 text-[#334155]" />
            ) : (
              <Menu className="w-5 h-5 text-[#334155]" />
            )}
          </button>

          {isOpen && (
            <div className="mt-2 bg-white border border-[#E2E8F0] shadow-sm rounded-xl overflow-hidden">
              {steps.map((step, index) => (
                <Link
                  key={step.id}
                  to={`${location.pathname}#${step.id}`}
                  onClick={handleLinkClick}
                  className={`block px-4 py-3 text-sm font-semibold transition border-b border-[#E2E8F0] last:border-b-0
                    ${
                      activeSection === step.id
                        ? "bg-[#0EA5E9] text-white"
                        : "text-[#334155] hover:bg-[#E0F2FE]"
                    }`}
                >
                  {step.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavBar;